/* eslint-disable react-hooks/exhaustive-deps */
import { Menu, Popover, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppWindow, ArrowLeftRight, Check, Database, IdCardLanyard, KeyRound, LayoutDashboard, PanelLeftClose, PanelLeftOpen, Router } from "lucide-react";
import {
  findOpenKeyFromPath,
  findSelectedKeyFromPath,
  getLocalStorage,
  getUserRole,
} from "../utils/helpers";
import pamNavigation from "../config/sidebarControls/mode/pamAccess";
import directoryNavigation from "../config/sidebarControls/mode/directoryAccess";
import superAdminNavigation from "../config/sidebarControls/role/superAdmin";
import { REACT_APP_ENV, REACT_APP_URL } from "../constants";
import ditNavigation from "../config/sidebarControls/mode/ditAccess";
import {
  dpaNavigation,
  tenantConfigNavigation,
  testingPamNavigation,
  windowNavigation,
} from "../config/sidebarControls/mode/simpleAccess";
import endUserNavigation from "../config/sidebarControls/role/endUserAccess";
import { availableMfaModes, getMfaMode, isMfaMode } from "../config/sidebarControls/mode/mfa";
import { AN_COLORS, AN_FONT, AN_MONO } from "../design-system/tokens";

// How each product mode reads in the switcher: an icon and what it is for.
const MODE_INFO = {
  mfa_ad: { Icon: IdCardLanyard, title: "Identity", desc: "Directories, users and Windows sign-in MFA" },
  mfa_db: { Icon: Database, title: "Database", desc: "MFA for database access" },
  mfa_radius: { Icon: Router, title: "RADIUS", desc: "MFA for VPN and network devices" },
  mfa_pam: { Icon: KeyRound, title: "PAM", desc: "Privileged SSH access to servers" },
  mfa_apps: { Icon: AppWindow, title: "Applications", desc: "Single sign-on and MFA for applications" },
};
const modeInfo = (key) =>
  MODE_INFO[key] || { Icon: ArrowLeftRight, title: getMfaMode(key)?.label || "Choose a mode", desc: "Switch product" };

function ModeSwitcher({ mode, onChange, collapsed }) {
  const [open, setOpen] = useState(false);
  const current = isMfaMode(mode) ? modeInfo(mode) : null;
  const CurrentIcon = current ? current.Icon : ArrowLeftRight;

  const list = (
    <div style={{ width: 260, display: "grid", gap: 2, fontFamily: AN_FONT }}>
      <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", color: AN_COLORS.muted, padding: "2px 8px 6px" }}>
        Switch product
      </div>
      {availableMfaModes().map((m) => {
        const info = modeInfo(m.key);
        const active = m.key === mode;
        return (
          <button
            key={m.key}
            type="button"
            className="an-mode-option"
            onClick={() => {
              setOpen(false);
              if (!active) onChange(m.key);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              padding: "8px",
              border: "none",
              borderRadius: 8,
              background: active ? AN_COLORS.primaryL : "transparent",
              cursor: active ? "default" : "pointer",
              textAlign: "left",
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: active ? AN_COLORS.primary : AN_COLORS.bg,
                color: active ? "#fff" : AN_COLORS.ink2,
              }}
            >
              <info.Icon size={16} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: AN_COLORS.ink }}>{info.title}</span>
              <span style={{ display: "block", fontSize: 11.5, color: AN_COLORS.muted }}>{info.desc}</span>
            </span>
            {active && <Check size={15} color={AN_COLORS.primary} />}
          </button>
        );
      })}
    </div>
  );

  const trigger = collapsed ? (
    <Tooltip title={current ? `${current.title} — switch product` : "Switch product"} placement="right">
      <button
        type="button"
        className="an-mode-switch"
        aria-label="Switch product"
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          border: `1px solid ${AN_COLORS.line}`,
          background: AN_COLORS.surface,
          color: AN_COLORS.primary,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <CurrentIcon size={17} />
      </button>
    </Tooltip>
  ) : (
    <button
      type="button"
      className="an-mode-switch"
      aria-label="Switch product"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "8px 10px",
        borderRadius: 10,
        border: `1px solid ${AN_COLORS.line}`,
        background: AN_COLORS.surface,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: AN_FONT,
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: AN_COLORS.primary,
          color: "#fff",
        }}
      >
        <CurrentIcon size={17} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: AN_COLORS.ink }}>
          {current ? current.title : "Choose a product"}
        </span>
        <span style={{ display: "block", fontSize: 11, color: AN_COLORS.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {current ? current.desc : "AD, Database, RADIUS, PAM or Applications"}
        </span>
      </span>
      <ArrowLeftRight size={15} color={AN_COLORS.muted} />
    </button>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement={collapsed ? "rightBottom" : "topLeft"}
      arrow={false}
      content={list}
    >
      {trigger}
    </Popover>
  );
}

function SideBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [selectedMenu, setSelectedMenu] = useState([]);
  const [mode, setMode] = useState(() => getLocalStorage("currentMode") || "mfa_ad");
  const userRole = getUserRole() || "";
  const layoutView =
    getLocalStorage("layoutView") || (userRole ? userRole.toLowerCase() : null);
  const [openMenu, setOpenMenu] = useState([]);
  // Collapsed to an icon rail. Remembered, because someone who reclaims the
  // space does not want it back on every page load.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem("sidebarCollapsed", String(next));
      } catch {
        /* private mode — the preference just does not persist */
      }
      return next;
    });
  const ENVIRONMENT = REACT_APP_ENV;
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    const onStorageChange = () => {
      setMode(getLocalStorage("currentMode") || "mfa_ad");
    };
    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, []);

  const sidebarHeader = useMemo(
    () => [
      {
        key: "/",
        icon: <LayoutDashboard className="icon" size={16} />,
        label: "Dashboard",
        navigate: "/",
      },
    ],
    []
  );

  useEffect(() => {
    let sidebarMenu = [];
    const isAdmin = userRole.toLowerCase() === "admin";
    const isSuperAdmin = userRole.toLowerCase() === "superadmin";

    if ((isAdmin && layoutView === "admin") || isSuperAdmin) {
      const hideHeader = isMfaMode(mode) && getMfaMode(mode)?.hideDashboardHeader;
      sidebarMenu = isAdmin && !hideHeader ? [...sidebarHeader] : [];
      if (isAdmin) {
        let adminNavigation = [];
        if (isMfaMode(mode)) {
          const entry = getMfaMode(mode);
          if (entry) adminNavigation = [...adminNavigation, ...entry.nav];
        } else if (mode === "pam") {
          adminNavigation = [...adminNavigation, ...pamNavigation];
        } else if (mode === "directory") {
          adminNavigation = [...adminNavigation, ...directoryNavigation];
        } else if (mode === "dit") {
          adminNavigation = [...adminNavigation, ...ditNavigation];
        } else if (mode === "tenantConfig") {
          adminNavigation = [...adminNavigation, ...tenantConfigNavigation];
        }
        if (
          REACT_APP_URL === "https://localhost:5173/" ||
          ENVIRONMENT === "testing"
        ) {
          if (mode === "window") {
            adminNavigation = [...adminNavigation, ...windowNavigation];
          }
          if (mode === "dpa") {
            adminNavigation = [...adminNavigation, ...dpaNavigation];
          }
          if (mode === "pam") {
            adminNavigation = [...adminNavigation, ...testingPamNavigation];
          }
        }
        sidebarMenu = [...sidebarMenu, ...adminNavigation];
      }
      if (isSuperAdmin) {
        sidebarMenu = [...sidebarMenu, ...superAdminNavigation];
      }
    } else {
      sidebarMenu = [...endUserNavigation];
    }
    setMenuItems(sidebarMenu);
  }, [userRole, mode, layoutView]);

  useEffect(() => {
    const paths = pathname.split("/").filter(Boolean);
    const pathKeys = paths.map(
      (_, index) => "/" + paths.slice(0, index + 1).join("/")
    );
    const openKey = findOpenKeyFromPath(pathKeys, menuItems);
    const selectedKey = findSelectedKeyFromPath(pathKeys, menuItems);
    if (openKey) {
      setSelectedMenu([selectedKey]);
      setOpenMenu([openKey]);
    } else {
      // Navigating to a TOP-LEVEL item leaves any open group alone. Clearing it
      // here meant that going from, say, Endpoints to Gateways collapsed
      // Targets, so coming back to Endpoint Users needed the group re-opened
      // every time. Collapsing is the user's call — handleOpenSubMenu still
      // does it when they click the group header itself.
      setSelectedMenu([selectedKey ?? pathname]);
    }
  }, [menuItems, pathname]);

  useEffect(() => {
    if (!isMfaMode(mode)) return;
    if (userRole.toLowerCase() !== "admin" || layoutView !== "admin") return;
    const entry = getMfaMode(mode);
    if (!entry) return;
    if (pathname === "/" || pathname === "") return;
    const allowed = entry.allowedPathPrefixes.some((p) => pathname.startsWith(p));
    if (!allowed) navigate(entry.defaultRoute);
  }, [mode, pathname, userRole, layoutView]);

  const handleOpenSubMenu = (keys) => {
    const latestOpenKey = keys.find((key) => !openMenu.includes(key));
    setOpenMenu(latestOpenKey ? [latestOpenKey] : []);
  };

  const handleMainMenu = (e) => {
    if (e.key) navigate(e.key);
  };

  const handleMfaModeChange = (nextKey) => {
    const entry = getMfaMode(nextKey);
    if (!entry) return;
    localStorage.setItem("currentMode", nextKey);
    window.dispatchEvent(new Event("storage"));
    setMode(nextKey);
    // Switching product always opens that product's dashboard.
    navigate(entry.dashboardRoute || "/");
  };

  const showMfaSwitcher =
    userRole.toLowerCase() === "admin" && layoutView === "admin";

  return (
    <div
      className="an-sidebar"
      style={{
        width: collapsed ? 60 : 208,
        transition: "width 160ms ease",
        flexShrink: 0,
        background: "var(--an-surface)",
        borderRight: `1px solid ${AN_COLORS.line}`,
        display: "flex",
        flexDirection: "column",
        alignSelf: "stretch",
        fontFamily: AN_FONT,
        overflow: "hidden",
      }}
    >
      <style>{`
        .an-sidebar .ant-menu {
          background: transparent !important;
          border-inline-end: none !important;
          font-family: ${AN_FONT};
        }
        .an-sidebar .ant-menu-item,
        .an-sidebar .ant-menu-submenu-title {
          height: 32px !important;
          line-height: 32px !important;
          margin: 1px 0 !important;
          padding: 0 10px !important;
          border-radius: 7px !important;
          color: ${AN_COLORS.ink2} !important;
        }
        .an-sidebar .ant-menu-title-content {
          font-size: 13px !important;
          font-weight: 400 !important;
          letter-spacing: 0 !important;
        }
        .an-sidebar .icon,
        .an-sidebar .ant-menu-item svg,
        .an-sidebar .ant-menu-submenu-title svg {
          width: 16px !important;
          height: 16px !important;
          flex-shrink: 0 !important;
        }
        .an-sidebar .ant-menu-item:hover:not(.ant-menu-item-selected),
        .an-sidebar .ant-menu-submenu-title:hover {
          background: ${AN_COLORS.bg} !important;
          color: ${AN_COLORS.ink} !important;
        }
        .an-sidebar .ant-menu-item-selected {
          background: ${AN_COLORS.primaryL} !important;
          color: ${AN_COLORS.primary} !important;
          position: relative;
        }
        .an-sidebar .ant-menu-item-selected .ant-menu-title-content {
          font-weight: 500 !important;
          color: ${AN_COLORS.primary} !important;
        }
        .an-sidebar .ant-menu-item-selected::before {
          content: "";
          position: absolute;
          left: -10px;
          top: 6px;
          bottom: 6px;
          width: 3px;
          border-radius: 2px;
          background: ${AN_COLORS.primary};
        }
        .an-sidebar .ant-menu-item-selected .icon,
        .an-sidebar .ant-menu-item-selected svg {
          color: ${AN_COLORS.primary} !important;
        }
        .an-sidebar .ant-menu-submenu-arrow {
          color: ${AN_COLORS.muted} !important;
        }
        .an-sidebar .ant-menu-sub.ant-menu-inline {
          background: transparent !important;
        }
        .an-sidebar .ant-menu-sub .ant-menu-item {
          padding-left: 38px !important;
        }
        .an-sidebar .ant-menu-sub .ant-menu-title-content {
          font-size: 12.5px !important;
        }
      `}</style>
        <div
          style={{
            flex: 1,
            overflow: "auto",
            padding: collapsed ? "14px 6px 10px" : "18px 10px 10px",
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "space-between",
              padding: collapsed ? "0 0 8px" : "4px 6px 6px 10px",
            }}
          >
            {!collapsed && (
              <span
                style={{
                  fontFamily: AN_MONO,
                  fontSize: 10.5,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: AN_COLORS.muted,
                  fontWeight: 500,
                }}
              >
                Navigate
              </span>
            )}
            <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                style={{
                  width: 26, height: 26, borderRadius: 6, border: "none", cursor: "pointer",
                  background: "transparent", color: AN_COLORS.muted,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
              </button>
            </Tooltip>
          </div>
          <Menu
            selectedKeys={selectedMenu}
            openKeys={openMenu}
            onOpenChange={handleOpenSubMenu}
            onClick={handleMainMenu}
            mode="inline"
            inlineCollapsed={collapsed}
            items={menuItems}
            style={{ border: "none" }}
          />
        </div>

        {showMfaSwitcher && (
          <div
            style={{
              padding: collapsed ? "10px 0" : 12,
              borderTop: `1px solid ${AN_COLORS.line}`,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ModeSwitcher mode={mode} onChange={handleMfaModeChange} collapsed={collapsed} />
          </div>
        )}
      </div>
  );
}

export default SideBar;
